/**
 * documentUtils provides standalone functions for accessing browser document APIs.
 * This improves testability by allowing easy mocking of document-dependent functionality.
 */

import {logger} from './Logger';
import {isValidString} from './stringUtils';
import {isFunction, isNullish} from './Utils';

/**
 * Get a property from document with vendor prefix support
 * Tries the standard property name first, then with webkit/moz/ms/o prefixes
 */
export const getDocumentPropertyWithPrefix = (prop: string): string | undefined => {
  if (isNullish(document)) {
    return undefined;
  }

  if (prop in document) {
    return prop;
  }

  const prefixes = ['webkit', 'moz', 'ms', 'o'];
  const pascalCase = prop.charAt(0).toUpperCase() + prop.slice(1);
  for (const prefix of prefixes) {
    const prefixedProp = prefix + pascalCase;
    if (prefixedProp in document) {
      return prefixedProp;
    }
  }

  return undefined;
};

/**
 * Check if the document is currently hidden (page visibility API)
 */
export const isDocumentHidden = (): boolean => {
  const hiddenProp = getDocumentPropertyWithPrefix('hidden');
  if (!isValidString(hiddenProp)) {
    return false;
  }

  return document[hiddenProp] === true;
};

/**
 * Check if a video element is currently in fullscreen mode
 */
export const isVideoInFullscreen = (): boolean => {
  const fullscreenProp = getDocumentPropertyWithPrefix('fullscreenElement');
  if (!isValidString(fullscreenProp)) {
    return false;
  }

  return document[fullscreenProp]?.nodeName === 'VIDEO';
};

/**
 * Add event listener to document
 *
 * Use this for document-level events that relate to the DOM structure and document state.
 * Some events ONLY fire on document (not on window):
 * - visibilitychange (& vendor-prefixed variants): tab/page visibility state
 * - fullscreenchange/fullscreenerror: fullscreen mode changes
 * - DOMContentLoaded: DOM parsed and ready (fires before window 'load')
 * - readystatechange: document.readyState changes
 *
 * Important: This function cannot be replaced with window.addEventListener because
 * the above events are document-specific and will not fire on window.
 *
 * We use vendor-prefixed events (webkitvisibilitychange, mozvisibilitychange, etc.)
 * for cross-browser compatibility, especially on older browsers and Smart TVs.
 */
export const addDocumentEventListener = (
  event: string,
  handler: EventListenerOrEventListenerObject,
  options?: boolean | AddEventListenerOptions,
): void => {
  if (isNullish(document) || !isFunction(document.addEventListener) || isNullish(handler)) {
    return;
  }

  // any-wrap needed to not confirm to type checks
  // we use vendor-prefixed events that are not part of the interface
  (document.addEventListener as any)(event, handler, options);
};

/**
 * Remove event listener from document
 *
 * @see addDocumentEventListener for details on when to use document vs window event listeners
 */
export const removeDocumentEventListener = (
  event: string,
  handler: EventListenerOrEventListenerObject,
  options?: boolean | EventListenerOptions,
): void => {
  if (isNullish(document) || !isFunction(document.removeEventListener) || isNullish(handler)) {
    return;
  }

  // any-wrap needed to not confirm to type checks
  // we use vendor-prefixed events that are not part of the interface
  (document.removeEventListener as any)(event, handler, options);
};

/**
 * Get a cookie value by name
 * @see https://www.w3schools.com/js/js_cookies.asp
 */
export const getCookie = (name: string): string | undefined => {
  if (isNullish(document)) {
    return undefined;
  }

  let allCookies: string[];
  try {
    allCookies = document.cookie.split(';');
  } catch (_error) {
    logger.log('failed to access cookie objects on document', _error);
    return undefined;
  }

  const cookieName = name + '=';
  for (const c of allCookies) {
    let cookie = c;
    while (cookie.charAt(0) === ' ') {
      cookie = cookie.substring(1); // trim spaces (if any) between ';' and next attribute
    }
    if (cookie.indexOf(cookieName) === 0) {
      return cookie.substring(cookieName.length, cookie.length);
    }
  }
  return '';
};

/**
 * Set a cookie with optional attributes
 */
export const setCookie = (
  name: string,
  value: string,
  attributes?: {domain?: string; expires?: string; maxAge?: number},
): void => {
  if (isNullish(document)) {
    return;
  }

  let newCookie = name + '=' + value + '; path=/';

  if (attributes?.domain) {
    newCookie += `; domain=${attributes.domain}`;
  }

  if (attributes?.expires) {
    newCookie += `; expires=${attributes.expires}`;
  }

  if (attributes?.maxAge) {
    newCookie += `; max-age=${attributes.maxAge}`;
  }

  try {
    document.cookie = newCookie;
  } catch (_error) {
    logger.log('failed to set cookie object on document', _error);
    return;
  }
};

/**
 * Delete a cookie by name
 * @see https://www.w3schools.com/js/js_cookies.asp
 */
export const deleteCookie = (name: string): void => {
  if (isNullish(document)) {
    return;
  }

  try {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
  } catch (_error) {
    logger.log('failed to delete cookies from document', _error);
    return;
  }
};
